import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { client } from '@/lib/api';
import { toast } from 'sonner';

interface ProjectInfo {
  person1Name: string | null;
  person2Name: string | null;
  weddingDate: string | null;
  guestCount: number | null;
  venueName: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemName: string;
  vendorEmail: string;
  vendorContactName: string | null;
  plannerName: string;
  plannerEmail: string;
  project: ProjectInfo;
  vendorId?: string | null;
  vendorContactId?: string | null;
}

function buildSubject(itemName: string, project: ProjectInfo): string {
  const couple =
    project.person1Name && project.person2Name
      ? `${project.person1Name} & ${project.person2Name}`
      : project.person1Name || project.person2Name || null;

  return couple
    ? `Inquiry: ${itemName} — ${couple} Wedding`
    : `Inquiry: ${itemName}`;
}

function buildBody(
  itemName: string,
  vendorContactName: string | null,
  plannerName: string,
  plannerEmail: string,
  project: ProjectInfo,
  customSignature: string | null,
): string {
  const greeting = vendorContactName ? `Hi ${vendorContactName},` : 'Hi there,';

  const couple =
    project.person1Name && project.person2Name
      ? `${project.person1Name} and ${project.person2Name}`
      : project.person1Name || project.person2Name || 'our clients';

  const details: string[] = [];
  if (project.weddingDate) {
    details.push(`- Date: ${new Date(project.weddingDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`);
  }
  if (project.guestCount) {
    details.push(`- Estimated Guest Count: ${project.guestCount}`);
  }
  if (project.venueName) {
    details.push(`- Venue: ${project.venueName}`);
  }

  const detailsBlock = details.length > 0 ? `\n\n${details.join('\n')}` : '';

  const signoff = customSignature && customSignature.trim() !== ''
    ? customSignature
    : `Thank you,\n${plannerName}\n${plannerEmail}`;

  return `${greeting}

My name is ${plannerName} and I'm the wedding planner for ${couple}.

We're interested in learning more about your ${itemName} services for their upcoming wedding.${detailsBlock}

Could you please send over your availability, packages, and pricing information?

${signoff}`;
}

export default function SendInquiryDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
  vendorEmail,
  vendorContactName,
  plannerName,
  plannerEmail,
  project,
  vendorId,
  vendorContactId,
}: Props) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: userSettings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to fetch user settings');
      return res.json();
    },
    enabled: open,
  });

  const signature = userSettings?.emailSignature ?? null;

  useEffect(() => {
    if (open) {
      setSubject(buildSubject(itemName, project));
      setBody(buildBody(itemName, vendorContactName, plannerName, plannerEmail, project, signature));
      setError(null);
    }
  }, [open, itemName, vendorContactName, plannerName, plannerEmail, project, signature]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await client.api.items[':id']['send-inquiry'].$post({
        param: { id: itemId },
        json: {
          subject,
          body,
          ...(vendorId ? { vendorId } : {}),
          ...(vendorContactId ? { vendorContactId } : {}),
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to send inquiry' }));
        throw new Error((err as { error?: string }).error || 'Failed to send inquiry');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success('Inquiry sent!');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Inquiry</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">To</label>
            <Input value={vendorEmail} disabled />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Message</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="font-mono text-sm"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Sent from Everplan on your behalf. Reply-to and CC set to {plannerEmail}.
          </p>

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={!subject.trim() || !body.trim() || sendMutation.isPending}
            >
              {sendMutation.isPending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
